import { useState } from 'react';
import { Link } from 'react-router-dom';

export function ArticleSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{title}</h2>
      <div className="prose-custom space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function ComparisonTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-background-100">
            {headers.map((h, i) => (
              <th key={i} className={`px-4 py-3 text-left font-semibold text-foreground-800 border-b border-background-200/70 ${i === 0 ? 'rounded-tl-lg' : ''} ${i === headers.length - 1 ? 'rounded-tr-lg' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-4 py-3 text-foreground-600 border-b border-background-200/50 whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ArticleFaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
      >
        <span className="text-sm font-medium text-foreground-800 pr-4">{question}</span>
        <i className={`ri-add-line text-foreground-500 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-45' : ''}`}></i>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="px-4 pb-3 text-sm text-foreground-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export function ArticleCTA({ title, description, primaryLabel, primaryHref, secondaryLabel, secondaryHref }: {
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryHref: string;
}) {
  const isExternal = primaryHref.startsWith('http');
  const isSecondaryExternal = secondaryHref.startsWith('http');
  return (
    <div className="mt-12 bg-primary-500 rounded-lg p-8 text-center">
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">{title}</h2>
      <p className="text-background-50/80 text-sm mb-6 max-w-lg mx-auto">{description}</p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {isExternal ? (
          <a href={primaryHref} target="_blank" rel="noopener nofollow" className="px-8 py-3 bg-background-50 text-foreground-900 rounded-full text-sm font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer">
            {primaryLabel}
            <i className="ri-arrow-right-up-line ml-2"></i>
          </a>
        ) : (
          <Link to={primaryHref} className="px-8 py-3 bg-background-50 text-foreground-900 rounded-full text-sm font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer">
            {primaryLabel}
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        )}
        {isSecondaryExternal ? (
          <a href={secondaryHref} target="_blank" rel="noopener nofollow" className="px-8 py-3 border border-background-50/40 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer">
            {secondaryLabel}
          </a>
        ) : (
          <Link to={secondaryHref} className="px-8 py-3 border border-background-50/40 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer">
            {secondaryLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

export function ArticleBreadcrumb({ current }: { current: string }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
      <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
      <i className="ri-arrow-right-s-line text-xs"></i>
      <span className="text-foreground-800 font-medium">{current}</span>
    </nav>
  );
}

export function ArticleHeader({ badge, readTime, title, description }: {
  badge: string;
  readTime: string;
  title: string;
  description: string;
}) {
  return (
    <header className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full">{badge}</span>
        <span className="text-xs text-foreground-500">{readTime}</span>
      </div>
      <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">{title}</h1>
      <p className="text-foreground-600 text-base leading-relaxed max-w-3xl">{description}</p>
    </header>
  );
}

export function ArticleRelatedLinks({ links }: { links: { label: string; href: string; external?: boolean }[] }) {
  return (
    <div className="mt-10 pt-8 border-t border-background-200/70">
      <p className="text-xs text-foreground-500 mb-3">Related Resources:</p>
      <div className="flex flex-wrap gap-2">
        {links.map((link, idx) => (
          <span key={link.href} className="flex items-center gap-2">
            {idx > 0 && <span className="text-foreground-300">·</span>}
            {link.external || link.href.startsWith('http') ? (
              <a href={link.href} target="_blank" rel="noopener nofollow" className="text-sm text-primary-600 hover:text-primary-700 hover:underline whitespace-nowrap">{link.label}</a>
            ) : (
              <Link to={link.href} className="text-sm text-primary-600 hover:text-primary-700 hover:underline whitespace-nowrap">{link.label}</Link>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}